/**
 * Every user-facing sentence in the Kinnser import report (EMR-1731).
 *
 * The report is the one place an agency user learns what an import did to a
 * chart. Its wording is settled product copy, not incidental UI text, so it
 * lives here and the tests assert it verbatim: changing a sentence is then a
 * deliberate act with a visible diff.
 *
 * Written in the chart register — one claim a sentence, no asides, no
 * decorative transitions. "Please" appears only in the error toast, where the
 * reader is being asked to do something.
 */

import type { ChartImportRunSummary } from '../../services/api/types.gen';

/** The control on the patient header, and the dialog's title. */
export const IMPORT_REPORT_LABEL = 'Import report';

/**
 * The button label while a run is still unreviewed.
 *
 * The count is the work waiting in Roger, so it rides on the button itself —
 * a reader should not have to open the report to learn there is nothing in it.
 */
export function importReportButtonLabel(actionCount: number): string {
  if (actionCount <= 0) return IMPORT_REPORT_LABEL;
  return `${IMPORT_REPORT_LABEL} · ${actionCount} to do`;
}

/** How each import verb reads to a clinician. */
export const RUN_VERB_LABELS: Record<ChartImportRunSummary['verb'], string> = {
  import: 'Import',
  overwrite: 'Overwrite',
  refresh: 'Refresh',
  refresh_preview: 'Refresh preview',
};

/** One chip in the run picker: which import, and when. */
export function runChipLabel(
  verb: ChartImportRunSummary['verb'],
  formattedDate: string
): string {
  return `${RUN_VERB_LABELS[verb]} · ${formattedDate}`;
}

/** Stands in for the picker when the chart has only ever been imported once. */
export function singleRunLabel(formattedDate: string): string {
  return `Imported ${formattedDate}`;
}

/**
 * Everything came across and nothing is waiting.
 *
 * Said as two short claims rather than one hedge, because "no issues found"
 * leaves a reader unsure whether the import ran at all.
 */
export const IMPORT_REPORT_EMPTY_COPY =
  'Everything came across. Nothing to do.';

/**
 * The run's counts in one line.
 *
 * Zero parts are omitted. A line reading "0 did not come across" invites the
 * reader to look for a list that is not there.
 */
export function runSummaryLine(counts: {
  action?: number;
  loss?: number;
  by_design?: number;
}): string {
  const parts: string[] = [];
  if (counts.action) parts.push(`${counts.action} to do`);
  if (counts.loss) parts.push(`${counts.loss} did not come across`);
  if (counts.by_design) parts.push(`${counts.by_design} done differently`);
  if (parts.length === 0) return IMPORT_REPORT_EMPTY_COPY;
  return parts.join(' · ');
}

/** A collapsed section names how much it is hiding. */
export function sectionCollapsedLabel(label: string, total: number): string {
  return `${label} (${total})`;
}

/** Opens the Kinnser page the finding came from. */
export const OPEN_IN_KINNSER_LABEL = 'Open in External EMR';

/** Expands or collapses a family of findings shown as one counted line. */
export function showItemsLabel(count: number, expanded = false): string {
  return expanded ? `Hide ${count}` : `Show ${count}`;
}

/** Hover text on the severity dot. Each severity asks for different work. */
export const SEVERITY_TITLES = {
  not_applied: 'Did not come across',
  caveat: 'Came across, verify',
} as const;

/** The report could not be read. The reader can retry; nothing is lost. */
export const IMPORT_REPORT_ERROR_COPY =
  'Unable to load the import report. Please try again.';

/** Records that a person read this run's report. */
export const MARK_REVIEWED_LABEL = 'Mark reviewed';

/** Who closed this run out, and when. */
export function reviewedByLine(
  name: string | null | undefined,
  formattedDate: string
): string {
  return `Reviewed by ${name ?? 'a colleague'} on ${formattedDate}`;
}

/** Toast when the review could not be recorded. */
export const MARK_REVIEWED_ERROR_COPY = 'Failed to mark the report reviewed';
